import { useState, useRef, type FC } from "react";

// Utils
import { getThemeStyles, Theme } from '../../../../../../../util/style';

// Model
import { Employee2 } from "../../../../../../../model/Employee";

// Lucide Icons
import { Camera } from "lucide-react";

interface FormUploadFieldProps {
    theme?: Theme;
    value: string;
    onChange: (url: string) => void;
    employee?: Employee2 | null;
}

export const FormUploadField: FC<FormUploadFieldProps> = ({
    theme = "light",
    value,
    onChange,
    employee,
}) => {
    const s = getThemeStyles(theme);

    const inputFileRef = useRef<HTMLInputElement>(null);

    const [isDragActive, setIsDragActive] = useState(false);
    const [isImageLoaded, setIsImageLoaded] = useState(false);

    const dropzoneClass = isDragActive
        ? "border-[var(--accent)] bg-blue-600/10"
        : theme === "dark"
            ? "border-[#30363d] bg-[#0d1117]/50 hover:border-[var(--accent)]"
            : "border-gray-300 bg-gray-50 hover:border-[var(--accent)]";

    const handleImageUpload = (file: File) => {
        if (!file.type.startsWith("image/")) return;

        setIsImageLoaded(false);

        const reader = new FileReader();

        reader.onload = ({ target }) => {
            onChange(target?.result as string);
        };

        reader.readAsDataURL(file);
    };

    const handleFileInputChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            handleImageUpload(files[0]);
        }
        return 1;
    };

    const handleDragOver = (
        event: React.DragEvent<HTMLDivElement>
    ) => {
        event.preventDefault();
        event.stopPropagation();
        return setIsDragActive(true);
    };

    const handleDragLeave = (
        event: React.DragEvent<HTMLDivElement>
    ) => { 
        event.preventDefault();
        event.stopPropagation();
        return setIsDragActive(false);
    };

    const handleFileDrop = (
        event: React.DragEvent<HTMLDivElement>
    ) => {
        const files = event.dataTransfer.files;
            if (files.length > 0) {
            handleImageUpload(files[0]);
        }

        event.preventDefault();
        event.stopPropagation();
        return setIsDragActive(false);
    };

    const getImageUrl = () => {
        if (!value) return "";

        const isLocalImage =
            value.startsWith("data:") ||
            value.includes("/storage/");

        if (isLocalImage) {
            return value;
        }

        return `${process.env.REACT_APP_API_URL}/storage/route/karyawan/${encodeURIComponent(
            String(employee?.nama)
        )}/${value}`;
    };

    const imageUrl = getImageUrl();

    const handleRemoveImage = (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
        event.stopPropagation();

        onChange("");
        setIsImageLoaded(false);

        if (inputFileRef.current) {
            inputFileRef.current.value = "";
        }
    };

    return (
        <div
            onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleFileDrop}
            onClick={() => inputFileRef.current?.click()}
            className={`relative w-full border-2 border-dashed rounded-lg p-6 cursor-pointer transition-all ${dropzoneClass}`}
        >
            <input ref={inputFileRef} type="file" accept="image/*" onChange={handleFileInputChange} className="hidden" />

            <div className="flex flex-col items-center justify-center gap-2">
                {value ? (
                    <>
                        <div className="relative w-32 h-32 rounded-full overflow-hidden">
                            {!isImageLoaded && (
                                <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                                    <span className="text-sm text-gray-500">Loading...</span>
                                </div>
                            )}

                            <img
                                src={imageUrl}
                                alt={String(employee?.nama)}
                                className="h-full w-full object-cover"
                                onLoad={() => setIsImageLoaded(true)}
                                onError={() => setIsImageLoaded(true)}
                            />
                        </div>
                        <div className="flex flex-col items-center align-center">
                            <p className={`${s.text} font-medium`}>
                                Foto berhasil dipilih
                            </p>
                            <p className={`${s.text} text-sm`}>
                                Klik atau seret foto baru ke sini untuk mengganti.
                            </p>
                            <button 
                                type="button"
                                className="bg-red-500 px-3 py-1 text-sm text-white font-bold rounded-lg mt-2 hover:bg-red-600"
                                onClick={handleRemoveImage}
                            >
                                Hapus Foto
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className={s.text}>
                            <Camera size={40} />
                        </div>
                        <p className={`${s.text} font-medium`}>
                            Seret foto ke sini atau klik untuk mengunggah.
                        </p>
                        <p className={`${s.text} text-sm`}>
                            Format yang didukung: JPG, PNG, GIF, dan WebP
                        </p>
                    </>
                )}
            </div>
        </div>
    );
};